import { useState } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { usePortalAuth } from '@/contexts/PortalAuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { InstallAppButton } from '@/components/customer/InstallAppButton';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';

// Dedicated login page for the staff self-service portal (separate entry from the /staff/login
// management console — same credentials, different destination).
export default function PortalLogin() {
  const { staff, login } = usePortalAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  if (staff) return <Navigate to="/portal" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate('/portal');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian text-white flex flex-col items-center justify-center px-6 py-10">
      <img src="/logo-emblem.png" alt="The Tiffin" className="w-20 h-20 rounded-full object-cover mb-4" />
      <h1 className="font-heading text-2xl font-700">Staff Portal</h1>
      <p className="text-sm text-white/50 mb-6">Sign in to view your info, documents &amp; activities</p>

      <form onSubmit={submit} className="w-full max-w-sm bg-white text-charcoal rounded-2xl p-6 space-y-4">
        <div className="space-y-1.5">
          <Label>Email</Label>
          <Input data-testid="portal-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@thetiffin.com" />
        </div>
        <div className="space-y-1.5">
          <Label>Password</Label>
          <div className="relative">
            <Input
              data-testid="portal-password-input"
              type={showPw ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="pr-10"
            />
            <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-ink">
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>
        <Button data-testid="portal-login-btn" type="submit" disabled={busy} className="w-full bg-saffron hover:bg-saffron-hover h-11 rounded-full">
          {busy ? 'Signing in…' : 'Sign In'}
        </Button>
      </form>

      <div className="mt-6 flex flex-col items-center gap-3">
        <InstallAppButton className="!border-white/30 !text-white hover:!bg-white/10" />
        <Link to="/staff/login" className="text-xs text-white/40 hover:text-white/70 underline" data-testid="portal-to-console-link">
          Manager / Admin console →
        </Link>
      </div>
    </div>
  );
}
